type ElementOptionsChild = string | HTMLElement | null | undefined | Node;

interface NormalElementOptions<TElement extends HTMLElement> {
  /** Properties to set */
  props?: Partial<TElement>;
  /** Attributes to set */
  attrs?: Record<string, string>;
  /** Events to subscribe to */
  on?: Record<string, (event: Event) => void>;
  /** CSS classes to add to the element */
  class?: string | readonly (string | null | undefined)[];
  /** Child elements to append, in provided order, to the element. */
  child?: ElementOptionsChild | readonly ElementOptionsChild[];
}

type ElementOptions<TElement extends HTMLElement> =
  | NormalElementOptions<TElement>
  | HTMLElement
  | string;

function normalizeOptions<TElement extends HTMLElement>(
  opts?: ElementOptions<TElement>,
): NormalElementOptions<TElement> {
  if (opts instanceof HTMLElement) {
    return { child: opts };
  }

  if (typeof opts === "string") {
    return { child: opts };
  }

  return opts ?? {};
}

/**
 * Helper function to create an element and initialize it.
 *
 * @param tagName
 * @returns The newly created element.
 */
export function el<Tag extends keyof HTMLElementTagNameMap>(
  tagName: Tag,
  options?: ElementOptions<HTMLElementTagNameMap[Tag]>,
): HTMLElementTagNameMap[Tag] {
  const el = document.createElement(tagName);
  options = normalizeOptions(options);

  for (const key in options.props) {
    if (options.props[key] === undefined) {
      continue;
    }

    el[key] = options.props[key];
  }

  for (const key in options.attrs) {
    el.setAttribute(key, options.attrs[key]);
  }

  for (const key in options.on) {
    el.addEventListener(key, options.on[key]);
  }

  if (typeof options.class === "string") {
    el.classList.add(options.class);
  } else if (options.class) {
    for (const cls of options.class) {
      if (cls) {
        el.classList.add(cls);
      }
    }
  }

  let children = options?.child
    ? Array.isArray(options.child)
      ? options.child
      : [options.child]
    : [];
  for (const child of children) {
    if (child !== null && child !== undefined) {
      el.append(child);
    }
  }

  return el;
}
